import type { BaseModel, Brand, Currency } from './application';
import type { Contract } from './contract';
import type { People } from './people';
import type { AnalyticsUser } from './user';

export interface Payment extends BaseModel {
  title: string;
  nfse_key: string;
  invoice: string;
  invoice_url?: string;
  contract_id: string;
  contract?: Contract;
  payment_order_id: string;
  // payment_order?: PaymentOrder;
  value: number;
  variable_value?: number;
  value_paid?: number;
  due_date: string;
  reference_month: string;
  status: PaymentStatus;
  type: PaymentType;
  category?: PaymentCategory;
  category_id: string;
  subcategory?: PaymentCategory;
  subcategory_id: string;
  brand: Brand;
  approved_by_id: number;
  approved_by?: AnalyticsUser;
  approved_at: string;
  denied_by_id: number;
  denied_by?: AnalyticsUser;
  receiver_id: string;
  receiver?: People;
  solicitant_id: number;
  solicitant?: AnalyticsUser;
  paid_by_id: number;
  paid_by?: AnalyticsUser;
  paid_at: string;
  issue_at: string;
  docs: PaymentDoc[];
  currency: Currency;
  cotation?: string | number;
  cotation_at?: string;
  tx_id?: string;
  wallet_for_payment?: string;
  value_in_usdt?: string;
  ia_analysis_id?: string;
  invoice_value?: number;
  invoice_pending_value?: number;
  // ia_analisys?: AiAnalysis;
}

export enum PaymentFilterDateField {
  DUE_DATE = 'due_date',
  ISSUE_AT = 'issue_at',
  PAID_AT = 'paid_at',
}

export enum PaymentStatus {
  PENDING_SOLICITATION = 'pending_solicitation',
  REJECTED_SOLICITATION = 'rejected_solicitation',
  PENDING_APPROVAL = 'pending_approval',
  PENDING_PAYMENT = 'pending_payment',
  PAID = 'paid',
  CANCELED = 'canceled',
  PROCESSING_PAYMENT = 'processing_payment',
}

export enum PaymentType {
  FIXED = 'fixed',
  VARIABLE = 'variable',
}

export interface PaymentCategory extends BaseModel {
  title: string;
  category_id: string;
}

export interface PaymentDoc {
  id: string;
  description: string;
  nfse_key: string;
  type: PaymentDocType;
  doc: string;
  account_payable_id: string;
  created_by: number;
}

export enum PaymentDocType {
  PROOF = 'proof',
  INVOICE = 'invoice',
  OTHER = 'other',
}

export interface PaymentMetrics {
  pending_approval_count: number;
  paid_current_month: number;
  percent_paid_current_month: number;
  due_accounts_current_month: number;
  due_next_7d_accounts_current_month: number;
  paid_current_week: number;
  period_current_week: string;
  qty_paid_actual_month: number;
  qty_paid_total_month: number;
  period_current_month: string;
  qty_created_today: number;
}

export interface PaymentDueMetrics {
  current_month: PaymentDueMetrics;
  next_month: PaymentDueMetrics;
  current_week: PaymentDueMetrics;
  next_week: PaymentDueMetrics;
}

export interface PaymentDueMetrics {
  overdue: number;
  paid: number;
  pending: number;
  total: number;
  period: string;
}

export interface PaymentEvolution {
  period: number;
  months: PaymentEvolutionMonth[];
}

export interface PaymentEvolutionMonth {
  month: string;
  total: number;
  variable: number;
  fixed: number;
}

export interface PaymentTodayIndicators {
  expected_payments_today: PaymentTodayIndicatorsItem;
  expected_payments_month: PaymentTodayIndicatorsItem;
  paid_today: PaymentTodayIndicatorsItem;
  paid_month: PaymentTodayIndicatorsItem;
  pending_payments_today: PaymentTodayIndicatorsItem;
  pending_payments_month: PaymentTodayIndicatorsItem;
  date: string;
  brand: Brand;
}

export interface PaymentTodayIndicatorsItem {
  value: number;
  count: number;
  description: string;
}

export interface CreatePaymentPayload {
  contract_id: string;
  receiver_id: string;
  category_id: string;
  subcategory_id: string;
  nfse_key: string;
  title: string;
  due_date: string;
  issue_at: string;
  value: number;
  reference_month: string;
  type: PaymentType;
  invoice: File;
  docs: File[];
  force_duplicate: boolean;
}

export interface UpdatePaymentPayload extends Partial<CreatePaymentPayload> {}

export interface PaymentByCategory {
  period: string;
  categories: { name: string; value: number }[];
}

export interface TotalPaymentsMetrics {
  brand: Brand;
  total_overdue: { count: number; value: number };
  total_paid: { count: number; value: number };
  total_payments: { count: number; value: number };
  total_pending_approval: { count: number; value: number };
  unpaid_not_overdue: { count: number; value: number };
}

export interface PaymentDuplicatedError {
  requires_confirmation: boolean;
  allow_if_different_nfse_key: boolean;
  duplicates: PaymentDuplicatedErrorData[];
  message_detail: string;
}

export interface PaymentDuplicatedErrorData {
  id: string;
  nfse_key: string;
  value: number;
  due_date: string;
  status: PaymentStatus;
}
